import React, { useState, useRef, useEffect } from "react";
import { useAddEmployee } from "../hooks/useAddEmployee";
import FormInput from "../components/UI/FormInput";
import FormSelect from "../components/UI/FormSelect";
import { useNavigate } from "react-router-dom";
import { FormProvider, useFormState } from "react-hook-form";
import InputDatePicker from "./EvaluationNew/components/InputDatePicker";
import ChangePasswordModal from "../components/Employees/ChangePasswordModal";
import { formatSSN, formatPhone, formatName } from "./Application/application.validation";
import {
  CloseIcon,
  UserIcon,
  MailIcon,
  BriefcaseIcon,
  IdCardIcon,
  MapPinIcon,
  ClockIcon,
  PhoneIcon,
  UserEditIcon,
} from "../Icons";
import useCurrentUser from "../hooks/useCurrentUser";
import CoverageAreaModal from "../components/Profile/CoverageAreaModal";
import AvailabilityModal from "../components/Profile/AvailabilityModal";
import { useUnsavedChangesBlocker } from "../hooks/useUnsavedChangesBlocker";
import UnsavedChangesDialog from "../components/UnsavedChangesDialog";
import {
  EMPLOYEE_BTN,
  EMPLOYEE_FORM,
} from "../components/Employees/employeeTheme";


const ProfileForm = () => {
  const navigate = useNavigate();
  const { userId, user: currentUser, isAdmin, isSurveyRole } = useCurrentUser()

  const saveAndLeavePending = useRef(false);
  const blockerRef = useRef(null);

  const { form, roles, loading, error, onSubmit, submitForm, isSubmitting } = useAddEmployee(
    () => {
      if (saveAndLeavePending.current) {
        saveAndLeavePending.current = false;
        blockerRef.current?.proceed();
      }
    },
    null,
    userId,
  );
  const {
    register,
    formState: { errors },
  } = form;
  const { isDirty } = useFormState({ control: form.control });
  const blocker = useUnsavedChangesBlocker({ when: isDirty });
  blockerRef.current = blocker;

  useEffect(() => {
    if (error && saveAndLeavePending.current) {
      saveAndLeavePending.current = false;
    }
  }, [error]);

  const handleSaveAndLeave = () => {
    saveAndLeavePending.current = true;
    submitForm(() => {
      saveAndLeavePending.current = false;
    })();
  };

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [coverageAreaModalOpen, setCoverageAreaModalOpen] = useState(false);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);

  const roleOptions = roles.map((r) => ({ value: r.id, label: r.name }));
  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];


  return (
    <div className={EMPLOYEE_FORM.page}>
      <div className={EMPLOYEE_FORM.shell}>
        <div className={EMPLOYEE_FORM.closeFloat}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={EMPLOYEE_BTN.close}
            aria-label="Close form"
          >
            <CloseIcon size={16} />
          </button>
        </div>
        <div className={EMPLOYEE_FORM.card}>
          <div className={EMPLOYEE_FORM.header}>
            <h1 className={EMPLOYEE_FORM.title}>Edit Profile</h1>
            {!isSurveyRole && (
              <div className={EMPLOYEE_FORM.headerActions}>
                <button
                  type="button"
                  onClick={() => setCoverageAreaModalOpen(true)}
                  className={EMPLOYEE_BTN.outline}
                >
                  <MapPinIcon className="w-4 h-4" />
                  Coverage Area
                </button>
                <button
                  type="button"
                  onClick={() => setAvailabilityModalOpen(true)}
                  className={EMPLOYEE_BTN.outline}
                >
                  <ClockIcon className="w-4 h-4" />
                  Availability
                </button>
              </div>
            )}
          </div>

          <FormProvider {...form}>
            <form onSubmit={onSubmit} className="space-y-8" noValidate autoComplete="off">
              <div className="space-y-8">
                {error && (
                  <div className={EMPLOYEE_FORM.errorBanner}>
                    <div className="text-red-500 mt-0.5">⚠️</div>
                    <div>
                      <h4 className="text-red-800 font-bold text-sm">
                        Submission Error
                      </h4>
                      <p className="text-red-700 text-sm mt-1">{error}</p>
                    </div>
                  </div>
                )}

                {/* Section 1: Account & Role */}
                <section>
                  <div className={EMPLOYEE_FORM.sectionHead}>
                    <span className={EMPLOYEE_FORM.step}>1</span>
                    <h4 className={EMPLOYEE_FORM.sectionTitle}>
                      Account Details
                    </h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormInput
                      label="First Name"
                      name="firstName"
                      register={register}
                      error={errors.firstName}
                      placeholder="Jane"
                      icon={UserIcon}
                      onInput={(e) => { e.target.value = formatName(e.target.value); }}
                      required
                      disabled
                    />
                    <FormInput
                      label="Last Name"
                      name="lastName"
                      register={register}
                      error={errors.lastName}
                      placeholder="Doe"
                      icon={UserIcon}
                      onInput={(e) => { e.target.value = formatName(e.target.value); }}
                      required
                      disabled
                    />
                    <FormInput
                      label="Email Address"
                      name="email"
                      type="text"
                      register={register}
                      error={errors.email}
                      placeholder="jane.doe@company.com"
                      icon={MailIcon}
                      autoComplete="new-email"
                      required
                      disabled
                    />

                    <FormSelect
                      label="Position / Role"
                      name="roleId"
                      register={register}
                      error={errors.roleId}
                      options={roleOptions}
                      placeholder={
                        roles.length ? "Select a Role" : "Loading roles..."
                      }
                      icon={BriefcaseIcon}
                      required
                      disabled
                    />

                    <FormSelect
                      label="Employment Status"
                      name="status"
                      register={register}
                      error={errors.status}
                      options={statusOptions}
                      icon={IdCardIcon}
                      required
                      disabled
                    />
                    <FormSelect
                      label="Work Mode"
                      name="workMode"
                      register={register}
                      error={errors.workMode}
                      options={[
                        { value: "WFO", label: "Office (WFO)" },
                        { value: "WFH", label: "Remote (WFH)" },
                      ]}
                      icon={BriefcaseIcon}
                      required
                      disabled
                    />
                  </div>
                </section>

                {/* Section 2: Personal Info */}
                <section>
                  <div className={EMPLOYEE_FORM.sectionHead}>
                    <span className={EMPLOYEE_FORM.step}>2</span>
                    <h4 className={EMPLOYEE_FORM.sectionTitle}>
                      Personal Information
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <InputDatePicker
                      label="Hire Date"
                      name="hireDate"
                      disabled
                      isRequired
                      formatSeparator="/"
                    />
                    <FormInput
                      label="SSN"
                      name="ssn"
                      register={register}
                      error={errors.ssn}
                      placeholder="XXX-XX-XXXX"
                      icon={IdCardIcon}
                      maxLength={11}
                      onInput={(e) => { e.target.value = formatSSN(e.target.value); }}
                    />
                    <InputDatePicker
                      label="Date of Birth"
                      name="dateOfBirth"
                      formatSeparator="/"
                    />
                    <FormInput
                      label="Mobile Number"
                      name="mobileNumber"
                      register={register}
                      error={errors.mobileNumber}
                      placeholder="(XXX)XXX-XXXX"
                      icon={PhoneIcon}
                      maxLength={13}
                      onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
                    />
                  </div>
                </section>

                {/* Section 3: Address */}
                <section>
                  <div className={EMPLOYEE_FORM.sectionHead}>
                    <span className={EMPLOYEE_FORM.step}>3</span>
                    <h4 className={EMPLOYEE_FORM.sectionTitle}>
                      Address Details
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <FormInput
                        label="Street Address"
                        name="address"
                        register={register}
                        error={errors.address}
                        placeholder="1234 Health Way, Suite 100"
                        icon={MapPinIcon}
                      />
                    </div>
                    <FormInput
                      label="City"
                      name="city"
                      register={register}
                      error={errors.city}
                      placeholder="San Francisco"
                    />
                    <FormInput
                      label="State"
                      name="state"
                      register={register}
                      error={errors.state}
                      placeholder="CA"
                    />
                    <FormInput
                      label="Zip Code"
                      name="zipCode"
                      register={register}
                      error={errors.zipCode}
                      placeholder="94103"
                    />
                    <FormInput
                      label="Country"
                      name="country"
                      register={register}
                      error={errors.country}
                      placeholder="USA"
                    />
                  </div>
                </section>
              </div>

              {/* Action Buttons */}
              <div className={EMPLOYEE_FORM.footer}>
                {/* Change Password Button - Left Bottom Corner (Admin Only) */}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setPasswordModalOpen(true)}
                    className={EMPLOYEE_BTN.outline}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    Change Password
                  </button>
                )}

                {/* Right Side Buttons */}
                <div className={EMPLOYEE_FORM.footerRight}>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className={EMPLOYEE_BTN.cancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={EMPLOYEE_BTN.primary}
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <UserEditIcon />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>

        {/* Change Password Modal */}
        {currentUser && (
          <ChangePasswordModal
            isOpen={passwordModalOpen}
            user={currentUser}
            onClose={() => setPasswordModalOpen(false)}
          />
        )}

        {/* Coverage Area Modal (hidden for SRVY role) */}
        {!isSurveyRole && (
          <CoverageAreaModal
            isOpen={coverageAreaModalOpen}
            onClose={() => setCoverageAreaModalOpen(false)}
            onSuccess={() => setCoverageAreaModalOpen(false)}
          />
        )}

        {!isSurveyRole && (
          <AvailabilityModal
            isOpen={availabilityModalOpen}
            onClose={() => setAvailabilityModalOpen(false)}
            onSuccess={() => setAvailabilityModalOpen(false)}
          />
        )}

        <UnsavedChangesDialog
          isOpen={blocker.state === "blocked" && isDirty}
          onDismiss={() => blocker.reset()}
          onSaveAndLeave={handleSaveAndLeave}
          onLeaveWithoutSaving={() => blocker.proceed()}
          isSaving={isSubmitting}
        />
      </div>
    </div>
  );
};

export default ProfileForm;
